import type { Column } from "@judo/model-api";
import { Sort } from "@judo/model-api";
import type { GridSortModel } from "@mui/x-data-grid";

/**
 * Build initial sort model for MUI DataGrid from table columns.
 * Filters columns with sort configuration and orders by sortPrecedence.
 *
 * When `multiColumnSort` is false (MUI Community DataGrid), the result is
 * limited to the single highest-precedence column to avoid the
 * "sortModel can only contain a single item" warning.
 */
export function buildInitialSortModel(columns: Column[] | undefined, multiColumnSort = false): GridSortModel {
	if (!columns) return [];

	const sorted = columns
		.filter((col) => col.sort && col.sort !== Sort.NONE)
		.toSorted((a, b) => (a.sortPrecedence ?? 0) - (b.sortPrecedence ?? 0))
		.map((col) => ({
			field: col.attributeType?.name ?? col.name,
			sort: col.sort === Sort.ASC ? ("asc" as const) : ("desc" as const),
		}));

	// Community DataGrid only supports single-column sorting
	if (!multiColumnSort && sorted.length > 1) {
		return sorted.slice(0, 1);
	}

	return sorted;
}

/**
 * Build QueryCustomizer._orderBy from table columns.
 * Used for initial data load with sorting.
 */
export function buildInitialOrderBy(columns: Column[] | undefined) {
	if (!columns) return undefined;

	const sortedColumns = columns
		.filter((col) => col.sort && col.sort !== Sort.NONE)
		.toSorted((a, b) => (a.sortPrecedence ?? 0) - (b.sortPrecedence ?? 0));

	if (sortedColumns.length === 0) return undefined;

	return sortedColumns.map((col) => ({
		attribute: col.attributeType?.name ?? col.name,
		descending: col.sort === Sort.DESC,
	}));
}

/**
 * Convert MUI GridSortModel to QueryCustomizer._orderBy format.
 * Used when user changes sort in DataGrid.
 */
export function convertSortModelToOrderBy(model: GridSortModel) {
	return model
		.filter((s) => s.sort)
		.map((s) => ({
			attribute: s.field,
			descending: s.sort === "desc",
		}));
}
